import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  beforeSrc: string;
  afterSrc: string;
  alt?: string;
  /** Seconds for one full sweep (left → right). Default 3.5s. */
  durationSec?: number;
  afterObjectPosition?: string;
  beforeObjectPosition?: string;
}

/**
 * Auto-animated before/after slider. Sweeps the divider back and forth so users
 * see the transformation without having to drag. Pauses on hover and respects
 * prefers-reduced-motion. Becomes interactive (drag) on hover/touch.
 */
export const AutoBeforeAfter = ({
  beforeSrc,
  afterSrc,
  alt = "",
  durationSec = 3.5,
  afterObjectPosition = "center",
  beforeObjectPosition = "center",
}: Props) => {
  const [pos, setPos] = useState(50);
  const [interactive, setInteractive] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || interactive) return;

    const tick = (t: number) => {
      if (startRef.current == null) startRef.current = t;
      const elapsed = (t - startRef.current) / 1000;
      // Triangle wave between 8 and 92 over 2*duration seconds
      const period = durationSec * 2;
      const phase = (elapsed % period) / period; // 0..1
      const tri = phase < 0.5 ? phase * 2 : 2 - phase * 2; // 0..1..0
      setPos(8 + tri * 84);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [durationSec, interactive]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl bg-muted shadow-soft"
      onMouseEnter={() => setInteractive(true)}
      onMouseLeave={() => setInteractive(false)}
      onTouchStart={() => setInteractive(true)}
    >
      <div className="relative aspect-[3/4] w-full">
        <img
          src={afterSrc}
          alt={`After ${alt}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: afterObjectPosition }}
        />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img
            src={beforeSrc}
            alt={`Before ${alt}`}
            className="h-full w-full object-cover"
            style={{
              width: `${(100 / pos) * 100}%`,
              maxWidth: "none",
              objectPosition: beforeObjectPosition,
            }}
          />
        </div>

        {/* slider line */}
        <div
          className="absolute inset-y-0 z-10 w-0.5 bg-white shadow-elevated"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow">
            <ChevronLeft className="h-4 w-4" />
            <ChevronRight className="h-4 w-4 -ml-1" />
          </div>
        </div>

        {/* labels */}
        <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white">
          Before
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">
          After
        </span>

        <input
          type="range"
          min={0}
          max={100}
          value={pos}
          onChange={(e) => {
            setInteractive(true);
            setPos(Number(e.target.value));
          }}
          aria-label="Comparison slider"
          className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
        />
      </div>
    </div>
  );
};
